import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Coins, 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  TrendingUp,
  TrendingDown,
  Globe,
  Building2,
  ToggleLeft,
  ToggleRight,
  RefreshCw
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface Token {
  id: string;
  symbol: string;
  name: string;
  network: string;
  contractAddress?: string;
  decimals: number;
  iconUrl?: string;
  currentPrice: number;
  priceChange24h: number;
  isActive: boolean;
  createdAt: string;
}

interface TenantToken {
  tenantId: string;
  tenantName: string;
  tokenId: string;
  isEnabled: boolean;
}

interface TokenFormData {
  symbol: string;
  name: string;
  network: string;
  contractAddress: string;
  decimals: number;
  iconUrl: string;
}

const NETWORKS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'bsc', label: 'BNB Chain' },
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'optimism', label: 'Optimism' },
  { value: 'avalanche', label: 'Avalanche' },
  { value: 'solana', label: 'Solana' },
];

const initialFormData: TokenFormData = {
  symbol: '',
  name: '',
  network: 'ethereum',
  contractAddress: '',
  decimals: 18,
  iconUrl: '',
};

export default function TokenManagement() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [formData, setFormData] = useState<TokenFormData>(initialFormData);

  // Fetch tokens
  const { data: tokens, isLoading } = useQuery({
    queryKey: ['tokens', searchTerm, networkFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (networkFilter) params.append('network', networkFilter);
      const response = await api.get(`/crypto/tokens?${params.toString()}`);
      return response.data as Token[];
    },
  });

  // Fetch tenant tokens for selected token
  const { data: tenantTokens } = useQuery({
    queryKey: ['tenant-tokens', selectedToken?.id],
    queryFn: async () => {
      if (!selectedToken) return [];
      const response = await api.get(`/crypto/tokens/${selectedToken.id}/tenants`);
      return response.data as TenantToken[];
    },
    enabled: !!selectedToken && showTenantModal,
  });

  // Add token mutation
  const addTokenMutation = useMutation({
    mutationFn: async (data: TokenFormData) => {
      const response = await api.post('/crypto/tokens', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      setShowAddModal(false);
      setFormData(initialFormData);
    },
  });

  // Update token mutation
  const updateTokenMutation = useMutation({
    mutationFn: async (data: { id: string; updates: Partial<TokenFormData> }) => {
      const response = await api.put(`/crypto/tokens/${data.id}`, data.updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      setShowEditModal(false);
      setSelectedToken(null);
      setFormData(initialFormData);
    },
  });

  // Delete token mutation
  const deleteTokenMutation = useMutation({
    mutationFn: async (tokenId: string) => {
      const response = await api.delete(`/crypto/tokens/${tokenId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  // Toggle token status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async (data: { tokenId: string; isActive: boolean }) => {
      const endpoint = data.isActive 
        ? `/crypto/tokens/${data.tokenId}/activate`
        : `/crypto/tokens/${data.tokenId}/deactivate`;
      const response = await api.post(endpoint);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  // Toggle tenant token mutation
  const toggleTenantTokenMutation = useMutation({
    mutationFn: async (data: { tokenId: string; tenantId: string; enable: boolean }) => {
      const endpoint = data.enable
        ? `/crypto/tokens/${data.tokenId}/enable`
        : `/crypto/tokens/${data.tokenId}/disable`;
      const response = await api.post(endpoint, { tenantId: data.tenantId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-tokens'] });
    },
  });

  // Refresh prices mutation
  const refreshPricesMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/crypto/tokens/refresh-prices');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const handleAddToken = () => {
    addTokenMutation.mutate(formData);
  };

  const handleUpdateToken = () => {
    if (selectedToken) {
      updateTokenMutation.mutate({
        id: selectedToken.id,
        updates: formData,
      });
    }
  };

  const openEditModal = (token: Token) => {
    setSelectedToken(token);
    setFormData({
      symbol: token.symbol,
      name: token.name,
      network: token.network,
      contractAddress: token.contractAddress || '',
      decimals: token.decimals,
      iconUrl: token.iconUrl || '',
    });
    setShowEditModal(true);
  };

  const openTenantModal = (token: Token) => {
    setSelectedToken(token);
    setShowTenantModal(true);
  };

  // Calculate stats
  const totalTokens = tokens?.length || 0;
  const activeTokens = tokens?.filter(t => t.isActive).length || 0;
  const networksCount = new Set(tokens?.map(t => t.network)).size;

  return (
    <PageContainer
      title="Gestão de Tokens"
      description="Gerencie os tokens disponíveis na plataforma"
      icon={Coins}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                <Coins className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Total de Tokens</p>
                <p className="text-xl font-bold text-surface-100">{totalTokens}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                <ToggleRight className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Tokens Ativos</p>
                <p className="text-xl font-bold text-success">{activeTokens}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Redes</p>
                <p className="text-xl font-bold text-surface-100">{networksCount}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <Button
              onClick={() => refreshPricesMutation.mutate()}
              disabled={refreshPricesMutation.isPending}
              className="w-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshPricesMutation.isPending ? 'animate-spin' : ''}`} />
              Atualizar Preços
            </Button>
          </div>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por símbolo ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
            <div className="w-48">
              <Select
                value={networkFilter}
                onChange={(e) => setNetworkFilter(e.target.value)}
                options={[
                  { value: '', label: 'Todas as redes' },
                  ...NETWORKS,
                ]}
              />
            </div>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Token
            </Button>
          </div>
        </div>
      </Card>

      {/* Tokens List */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">Tokens Cadastrados</h3>
          {isLoading ? (
            <div className="text-center py-8 text-surface-400">Carregando...</div>
          ) : tokens && tokens.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-700">
                    <th className="text-left py-3 px-4 text-surface-400 font-medium">Token</th>
                    <th className="text-left py-3 px-4 text-surface-400 font-medium">Rede</th>
                    <th className="text-right py-3 px-4 text-surface-400 font-medium">Preço</th>
                    <th className="text-right py-3 px-4 text-surface-400 font-medium">24h</th>
                    <th className="text-center py-3 px-4 text-surface-400 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-surface-400 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <tr key={token.id} className="border-b border-surface-800 hover:bg-surface-800/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-sm font-bold text-primary-400">
                            {token.iconUrl ? (
                              <img src={token.iconUrl} alt={token.symbol} className="w-8 h-8 rounded-full" />
                            ) : (
                              token.symbol.slice(0, 2)
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-surface-100">{token.symbol}</p>
                            <p className="text-sm text-surface-400">{token.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="info">{token.network}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-surface-100 font-medium">{formatCurrency(token.currentPrice)}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`flex items-center justify-end gap-1 ${token.priceChange24h >= 0 ? 'text-success' : 'text-error'}`}>
                          {token.priceChange24h >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          {formatPercent(token.priceChange24h)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => toggleStatusMutation.mutate({ tokenId: token.id, isActive: !token.isActive })}
                          disabled={toggleStatusMutation.isPending}
                          className="inline-flex items-center"
                        >
                          {token.isActive ? (
                            <ToggleRight className="w-6 h-6 text-success" />
                          ) : (
                            <ToggleLeft className="w-6 h-6 text-surface-500" />
                          )}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openTenantModal(token)}
                            title="Gerenciar por Tenant"
                          >
                            <Building2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(token)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Deseja excluir o token ${token.symbol}?`)) {
                                deleteTokenMutation.mutate(token.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-error" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-surface-400">
              Nenhum token cadastrado
            </div>
          )}
        </div>
      </Card>

      {/* Add Token Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setFormData(initialFormData);
        }}
        title="Adicionar Token"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Símbolo"
              placeholder="ETH"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
            />
            <Input
              label="Nome"
              placeholder="Ethereum"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <Select
            label="Rede"
            value={formData.network}
            onChange={(e) => setFormData({ ...formData, network: e.target.value })}
            options={NETWORKS}
          />
          <Input
            label="Endereço do Contrato (opcional)"
            placeholder="0x..."
            value={formData.contractAddress}
            onChange={(e) => setFormData({ ...formData, contractAddress: e.target.value })}
          />
          <Input
            type="number"
            label="Decimais"
            value={formData.decimals.toString()}
            onChange={(e) => setFormData({ ...formData, decimals: parseInt(e.target.value) || 18 })}
          />
          <Input
            label="URL do Ícone (opcional)"
            placeholder="https://..."
            value={formData.iconUrl}
            onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
          />
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                setFormData(initialFormData);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddToken}
              disabled={!formData.symbol || !formData.name || addTokenMutation.isPending}
              className="flex-1"
            >
              {addTokenMutation.isPending ? 'Salvando...' : 'Adicionar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Token Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedToken(null);
          setFormData(initialFormData);
        }}
        title={`Editar Token - ${selectedToken?.symbol}`}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Símbolo"
              placeholder="ETH"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
            />
            <Input
              label="Nome"
              placeholder="Ethereum"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <Select
            label="Rede"
            value={formData.network}
            onChange={(e) => setFormData({ ...formData, network: e.target.value })}
            options={NETWORKS}
          />
          <Input
            label="Endereço do Contrato (opcional)"
            placeholder="0x..."
            value={formData.contractAddress}
            onChange={(e) => setFormData({ ...formData, contractAddress: e.target.value })}
          />
          <Input
            type="number"
            label="Decimais"
            value={formData.decimals.toString()}
            onChange={(e) => setFormData({ ...formData, decimals: parseInt(e.target.value) || 18 })}
          />
          <Input
            label="URL do Ícone (opcional)"
            placeholder="https://..."
            value={formData.iconUrl}
            onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
          />
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                setSelectedToken(null);
                setFormData(initialFormData);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateToken}
              disabled={!formData.symbol || !formData.name || updateTokenMutation.isPending}
              className="flex-1"
            >
              {updateTokenMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Tenant Token Management Modal */}
      <Modal
        isOpen={showTenantModal}
        onClose={() => {
          setShowTenantModal(false);
          setSelectedToken(null);
        }}
        title={`Gerenciar ${selectedToken?.symbol} por Tenant`}
      >
        <div className="space-y-4">
          <p className="text-surface-400 text-sm">
            Habilite ou desabilite este token para cada tenant individualmente.
          </p>
          {tenantTokens && tenantTokens.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {tenantTokens.map((tt) => (
                <div
                  key={tt.tenantId}
                  className="flex items-center justify-between p-3 bg-surface-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-surface-400" />
                    <span className="text-surface-100">{tt.tenantName}</span>
                  </div>
                  <button
                    onClick={() => toggleTenantTokenMutation.mutate({
                      tokenId: selectedToken!.id,
                      tenantId: tt.tenantId,
                      enable: !tt.isEnabled,
                    })}
                    disabled={toggleTenantTokenMutation.isPending}
                    className="inline-flex items-center"
                  >
                    {tt.isEnabled ? (
                      <ToggleRight className="w-6 h-6 text-success" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-surface-500" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-surface-400">
              Nenhum tenant encontrado
            </div>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setShowTenantModal(false);
              setSelectedToken(null);
            }}
            className="w-full"
          >
            Fechar
          </Button>
        </div>
      </Modal>
    </PageContainer>
  );
}
